import { useState } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { createUser } from '../backend/services/userAdmin.ts';

const BRAND_GREEN = '#1D9E75';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: BRAND_GREEN },
    '&.Mui-focused fieldset': { borderColor: BRAND_GREEN },
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    '&.Mui-focused': { color: BRAND_GREEN },
  },
};

export default function CreateUserPage() {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [message,  setMessage]  = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleBack = () => navigate('/users');

  const handleSubmit = async () => {
    try {
      const data = await createUser(username, email, password, isAdmin ? 'admin' : undefined);
      if (data) {
        setMessage({ type: 'success', text: 'User created successfully.' });
        setUsername(''); setEmail(''); setPassword(''); setIsAdmin(false);
        setTimeout(handleBack, 1000);
      } else {
        setMessage({ type: 'error', text: 'Failed to create user.' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to create user.' });
    }
  };

  return (
    <Box sx={{ p: '0 1.5rem 2.5rem', width: '100%', height: '100vh', boxSizing: 'border-box', alignItems: 'center' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
        <Button
          size="small"
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: '14px !important', padding: '0 !important' }} />}
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            textTransform: 'uppercase',
            color: 'var(--color-text-secondary)',
            borderColor: 'rgba(255,255,255,0.12)',
            borderRadius: 2,
            '&:hover': { borderColor: BRAND_GREEN, color: BRAND_GREEN },
          }}
          variant="outlined"
        >
          Back
        </Button>
        <Typography sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 20,
          fontWeight: 600,
          color: BRAND_GREEN,
        }}>
          Create User
        </Typography>
      </Box>

      {/* Form panel */}
      <Box sx={{
        background:   'var(--color-bg-secondary)',
        borderRadius: 3,
        p:            '1.5rem 2rem',
        maxWidth:     480,
      }}>
        {message && (
          <Alert
            severity={message.type}
            sx={{ mb: 2.5, borderRadius: 2, fontFamily: "'DM Mono', monospace", fontSize: 12 }}
          >
            {message.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />
          <TextField
            label="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAdmin}
                onChange={e => setIsAdmin(e.target.checked)}
                sx={{
                  color: 'rgba(255,255,255,0.2)',
                  '&.Mui-checked': { color: BRAND_GREEN },
                  '& svg': { fontSize: '18px !important', padding: '0 !important' },
                }}
              />
            }
            label={
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--color-text-primary)' }}>
                Admin user
              </Typography>
            }
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              background: BRAND_GREEN,
              borderRadius: 2,
              py: 1.25,
              mt: 0.5,
              '&:hover': { background: '#17876A' },
            }}
          >
            Create User
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
